<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div class="device-icon-pin" :class="sizeClass" :title="device.deviceName">
      <!-- The reading's own drawing when it has one (P13), else the drawn
           activity glyph; the ring carries the reading against its range. -->
      <ControlStatusRing :value="ringValue">
        <ControlIcon :name="pinIconName" :size="pinIconSize" />
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <!-- Card face: the reading's own drawing, the device's name and its number;
         the largest size lists every reading the device reports. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      accent="rgb(45, 212, 191)"
      class="sensor-card-face"
      @click="emit('open-control-modal')"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <!-- Raw <img> rather than DeviceThumbnail: that component is a fixed 2rem
             circle for the compact tile, which left the card's photo tiny. -->
        <img v-if="thumbUrl" :src="thumbUrl" alt="" class="sensor-card-face__img" draggable="false" />
        <!-- The device's own 3D model, drawn from its slot (settings.model3d):
             a tank reads as the tank standing in the 3D view, not as a glyph.
             The sensor illustration below stays the fallback. -->
        <img
          v-else-if="modelPreviewUrl"
          :src="modelPreviewUrl"
          alt=""
          class="sensor-card-face__img sensor-card-face__img--model"
          draggable="false" />
        <!-- A temperature's number IS its picture: the degrees fill the media
             box instead of the thermometer drawing (owner, 27.10.2026). -->
        <span v-else-if="leadsWithReading && cardLeadReading" class="sensor-card-face__reading">
          <span class="sensor-card-face__reading-value">{{ cardLeadReading.value }}</span>
          <span v-if="cardLeadReading.unit" class="sensor-card-face__reading-unit">{{ cardLeadReading.unit }}</span>
        </span>
        <SensorAnimation
          v-else-if="sensorKind !== 'generic'"
          :kind="sensorKind"
          :value="primaryValue"
          :unit="primaryUnit" />
        <span v-else class="material-symbols-outlined sensor-card-face__glyph">{{ device.icon || 'sensors' }}</span>
      </template>

      <template v-if="cardSize !== 'min' && (!leadsWithReading || cardSummary.length > 0)" #value>
        <span class="sensor-card-face__value">
          <template v-if="cardSummary.length > 0">
            <span v-for="(item, index) in cardSummary" :key="index">
              {{ item.value }}<span v-if="item.unit">{{ item.unit }}</span>
              <span v-if="index < cardSummary.length - 1" class="mx-1">•</span>
            </span>
          </template>
          <span v-else>—</span>
        </span>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'sensors'"
      :name="device.deviceName"
      :is-on="false"
      active-color="amber-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <div class="sensor-sheet">
      <!-- One row per reading: what it is, what it says now, how far along its range -->
      <div v-for="(value, key) in sensorValues" :key="key" class="sensor-reading">
        <div class="sensor-reading-head">
          <span class="sensor-reading-label">
            <ControlIcon :name="getSensorIcon(key)" size="1.05em" />
            <span class="sensor-reading-name">{{ formatPropertyName(key) }}</span>
          </span>
          <span v-if="getSensorUnit(key)" class="sensor-reading-unit">{{ getSensorUnit(key) }}</span>
        </div>
        <div class="sensor-reading-value">{{ formatSensorValue(value) }}</div>
        <!-- The reading's own level: a slim bar, coloured by the reading's range -->
        <div v-if="typeof value === 'number' && getSensorRange(key)" class="sensor-reading-bar">
          <div
            class="sensor-reading-bar__fill"
            :class="getSensorColorClass(key, value)"
            :style="{ width: `${getSensorPercentage(key, value)}%` }"></div>
        </div>
        <!-- Boolean status indicator -->
        <div v-else-if="typeof value === 'boolean'" class="sensor-reading-state">
          <span class="sensor-reading-dot" :class="{ 'sensor-reading-dot--on': value }"></span>
          {{ value ? tt('Active', 'Aktivno') : tt('Inactive', 'Neaktivno') }}
        </div>
      </div>

      <!-- Value history logs -->
      <div v-if="loggableValues.length" class="sensor-history">
        <p class="sensor-history-title">
          <ControlIcon name="activity" size="1em" />
          {{ tt('History', 'Zgodovina') }}
        </p>
        <div class="sensor-history-rows">
          <ValueLogButton
            v-for="v in loggableValues"
            :key="v.id"
            :device-value="v"
            :device="{ id: device.id, deviceName: device.deviceName }" />
        </div>
      </div>

      <p v-if="lastUpdated" class="sensor-updated">
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
      </p>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import DeviceCardShell from './DeviceCardShell.vue';
import { getDefaultThumbnailUrl } from '../../composables/useDeviceThumbnail';
import { useDeviceModelThumbnail } from '../../composables/useDeviceModelThumbnail';
import { useDeviceValues } from '../../composables/useDeviceValues';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import SensorAnimation from './sensor/SensorAnimation.vue';
import { sensorKindOf } from './deviceWidgetTypes';
import ValueLogButton from './ValueLogButton.vue';
import { tt } from '../../utils/consoleText';
import { parseDeviceModel, type DeviceModel3d } from '@/utils/bimDeviceModel';
import { getTankLevelPct } from '@/utils/waterTankDevices';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

const loggableValues = computed(() =>
  (props.device.deviceValues ?? []).filter((v) => v.loggingType && v.loggingType !== 'None'),
);

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

// Control mode: full record of all values grouped by display name
const sensorValues = computed(() => {
  const sensors: Record<string, unknown> = {};
  if (!props.device.deviceValues) return sensors;
  props.device.deviceValues.forEach((v) => {
    let value = currentValues.value[v.valueKey] ?? v.currentValue;
    if (value !== null && value !== undefined) {
      if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
        try {
          value = JSON.parse(value);
        } catch {
          /* keep as string */
        }
      }
      sensors[v.name || v.valueKey] = value;
    }
  });
  return sensors;
});

// Button mode: up to 2 formatted values
const sensorSummary = computed(() => {
  if (!props.device.deviceValues) return [];
  return props.device.deviceValues
    .filter((v) => {
      const val = v.valueKey in currentValues.value ? currentValues.value[v.valueKey] : v.currentValue;
      return val !== undefined && val !== null;
    })
    .map((v) => {
      const raw = v.valueKey in currentValues.value ? currentValues.value[v.valueKey] : v.currentValue;
      return {
        key: v.valueKey,
        name: v.name || v.valueKey,
        value: typeof raw === 'number' ? String(Math.round(raw * 10) / 10) : String(raw),
        unit: v.unit || '',
      };
    })
    .slice(0, 2);
});

/**
 * Which illustration the tile draws in place of the glyph — resolved from the
 * reading's unit and the device's name and icon (see `sensorKindOf`), so the tile
 * draws what the number means: a charge, a mercury column, a wind vane.
 */
const sensorKind = computed(() =>
  sensorKindOf({
    icon: props.device.icon,
    deviceName: props.device.deviceName,
    shortName: props.device.shortName,
    deviceType: props.device.deviceType,
    controlType: props.device.deviceControl || props.device.controlType,
    unit: primaryUnit.value,
    valueNames: (props.device.deviceValues ?? []).flatMap((v) => [v.name, v.valueKey]),
    settings: props.device.settings,
  }),
);

const primaryUnit = computed(() => (props.device.deviceValues ?? [])[0]?.unit ?? '');

/** The value the tile leads with — the one every level in the drawing follows. */
const primaryValue = computed(() => {
  const [first] = sensorSummary.value;
  const numeric = first ? Number(first.value) : Number.NaN;
  return Number.isFinite(numeric) ? numeric : null;
});

/**
 * The reading the card leads with when the number is the picture: the device's
 * temperature when it reports one, else its first reading.
 */
const cardLeadReading = computed(() => {
  const readings = sensorSummary.value;
  return readings.find((reading) => /temp|°\s*[cf]/i.test(`${reading.name} ${reading.unit}`)) ?? readings[0] ?? null;
});

/**
 * A temperature's card face is its number, not the thermometer drawing (owner,
 * 27.10.2026: *"temperature_sensor should not display a svg (preset) on device
 * cards.. it should display temperature reading. with big digits"*). Every other
 * reading keeps its illustration.
 */
const leadsWithReading = computed(() => sensorKind.value === 'temperature' && cardLeadReading.value !== null);

/** The value row keeps only the readings the big figure does not already show. */
const cardSummary = computed(() =>
  leadsWithReading.value
    ? sensorSummary.value.filter((reading) => reading !== cardLeadReading.value)
    : sensorSummary.value,
);

/**
 * The device's OWN 3D model (`settings.model3d`, card t_6b2c5f01) — the model
 * the operator picked in the device editor. When it has one, the card draws the
 * model instead of the reading's illustration; the illustration stays as the
 * fallback, never as a second opinion beside the model.
 */
const deviceModel = computed<DeviceModel3d | null>(() => {
  const settings = (props.device as { settings?: unknown }).settings;
  return settings ? parseDeviceModel(settings) : null;
});

/** A tank's model follows its live level — the water line IS the model. */
const modelLevel = computed(() => {
  const type = deviceModel.value?.library?.type;
  if (type !== 'water-tank' && type !== 'waste-tank') return null;
  // The same reader the 3D layer uses, with the socket-fed overlay the card's
  // own number comes from — so picture and percentage never disagree.
  return getTankLevelPct(props.device, currentValues.value);
});

/** The card's picture of that model (drawn offscreen, cached by the slot's key). */
const modelPreviewUrl = useDeviceModelThumbnail(deviceModel, modelLevel);

/** The reading's affordance, in the sheet's drawn set (`ControlIcon` names). */
function getSensorIcon(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('temp')) return 'thermometer';
  if (n.includes('humidity') || n.includes('moisture')) return 'drop';
  if (n.includes('co2') || n.includes('carbon')) return 'wind';
  if (n.includes('motion') || n.includes('occupancy')) return 'person';
  if (n.includes('light') || n.includes('lux')) return 'warm';
  if (n.includes('pressure')) return 'activity';
  if (n.includes('voltage') || n.includes('current') || n.includes('power')) return 'bolt';
  if (n.includes('energy')) return 'bolt';
  return 'activity';
}

function getSensorUnit(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('temp')) return '°C';
  if (n.includes('humidity')) return '%';
  if (n.includes('co2')) return 'ppm';
  if (n.includes('pressure')) return 'hPa';
  if (n.includes('voltage')) return 'V';
  if (n.includes('current')) return 'A';
  if (n.includes('power')) return 'W';
  if (n.includes('energy')) return 'kWh';
  if (n.includes('lux')) return 'lx';
  return '';
}

function getSensorRange(name: string): { min: number; max: number } | null {
  const n = name.toLowerCase();
  if (n.includes('temp')) return { min: -10, max: 40 };
  if (n.includes('humidity')) return { min: 0, max: 100 };
  if (n.includes('co2')) return { min: 400, max: 2000 };
  if (n.includes('pressure')) return { min: 950, max: 1050 };
  if (n.includes('lux')) return { min: 0, max: 1000 };
  return null;
}

function getSensorPercentage(name: string, value: number): number {
  const range = getSensorRange(name);
  if (!range) return 0;
  return Math.max(0, Math.min(100, ((value - range.min) / (range.max - range.min)) * 100));
}

function getSensorColorClass(name: string, value: number): string {
  const n = name.toLowerCase();
  const pct = getSensorPercentage(name, value);
  if (n.includes('co2')) return pct < 40 ? 'bg-green-500' : pct < 70 ? 'bg-yellow-500' : 'bg-red-500';
  if (n.includes('temp')) return pct < 35 ? 'bg-blue-500' : pct < 65 ? 'bg-green-500' : 'bg-red-500';
  if (n.includes('humidity')) return pct < 30 || pct > 70 ? 'bg-yellow-500' : 'bg-green-500';
  return 'bg-primary-500';
}

function formatPropertyName(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function formatSensorValue(value: unknown): string {
  if (value === null || value === undefined) return '--';
  if (typeof value === 'number') return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** The card's picture, resolved through the S3-key blob cache (the one door
 *  the other card faces read `thumbnailUrl` through). */
const thumbUrl = computed(() => getDefaultThumbnailUrl(props.device));

const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The pin's own reading (P13): the first one the device publishes. */
const primaryReading = computed<{ key: string; value: unknown } | null>(() => {
  const [entry] = Object.entries(sensorValues.value);
  return entry === undefined ? null : { key: entry[0], value: entry[1] };
});

/** The ring's arc: the reading against its own range, when it has one. */
const ringValue = computed(() => {
  const reading = primaryReading.value;
  if (!reading || typeof reading.value !== 'number') return null;
  return getSensorPercentage(reading.key, reading.value);
});

/** The pin's affordance, off the reading's own fold (the sheet's rows use it too). */
const pinIconName = computed(() => (primaryReading.value ? getSensorIcon(primaryReading.value.key) : 'activity'));

/** The drawn icon's size inside the pin, in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});
</script>

<style scoped>
/* ── Control mode: the sheet's reading rows ───────────────────────────────
   One row per reading: its affordance and name, the unit as a chip, the value
   big, and the level as a slim bar in the reading's own colour. The tokens are
   the `device-sheet` primitives' own recipes, so the row sits in the sheet
   language under any glass / roundness / theme preset. */
.sensor-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(0.75rem * var(--ui-space-factor));
}

.sensor-reading {
  display: flex;
  flex-direction: column;
  gap: calc(0.375rem * var(--ui-space-factor));
  padding: calc(0.75rem * var(--ui-space-factor)) calc(0.875rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: calc(0.875rem * var(--ui-radius-factor));
  background: rgba(148, 163, 184, 0.08);
}

.dark .sensor-reading {
  background: rgba(255, 255, 255, 0.03);
}

.sensor-reading-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
}

.sensor-reading-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgb(var(--gray-500));
}

.dark .sensor-reading-label {
  color: rgb(var(--gray-400));
}

.sensor-reading-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sensor-reading-unit {
  flex-shrink: 0;
  padding: 0.0625rem 0.4375rem;
  border: 1px solid var(--glass-edge);
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.12);
  font-size: 0.6875rem;
  font-weight: 600;
  color: rgb(var(--gray-500));
}

.dark .sensor-reading-unit {
  background: rgba(255, 255, 255, 0.05);
  color: rgb(var(--gray-400));
}

.sensor-reading-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
}

.sensor-reading-bar {
  height: 0.25rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.22);
  overflow: hidden;
}

.dark .sensor-reading-bar {
  background: rgba(255, 255, 255, 0.1);
}

.sensor-reading-bar__fill {
  height: 100%;
  border-radius: inherit;
  transition: width 0.7s ease-out;
}

.sensor-reading-state {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: rgb(var(--gray-500));
}

.dark .sensor-reading-state {
  color: rgb(var(--gray-400));
}

.sensor-reading-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.55);
}

.sensor-reading-dot--on {
  background: var(--sheet-accent, var(--device-color-light));
}

.sensor-history {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: calc(0.75rem * var(--ui-space-factor));
  border-top: 1px solid var(--glass-edge-soft);
}

.sensor-history-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .sensor-history-title {
  color: rgb(var(--gray-400));
}

.sensor-history-rows {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.sensor-updated {
  margin: 0;
  text-align: center;
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .sensor-updated {
  color: rgb(var(--gray-400));
}

/* ── Card face ───────────────────────────────────────────────────────────────
   The shell owns the frame, the name and the size rules; these are the device's
   own parts inside it. */
.sensor-card-face__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* The device's own 3D model, drawn by `utils/deviceModelThumbnail.ts`: the
   renderer frames it tight, so a hair of padding keeps the tank off the edge. */
.sensor-card-face__img--model {
  padding: 2px;
  box-sizing: border-box;
}

/* The same picture inside the compact tile's icon circle. */
.device-button .device-icon--circle .sensor-tile__model {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 1px;
  box-sizing: border-box;
}

.sensor-card-face__glyph {
  font-size: 2.4rem;
}

/* A temperature card's picture: the degrees themselves, scaled off the media
   box the drawing used to fill, so the number stays big at every card size. */
.sensor-card-face__reading {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.12em;
  font-size: max(1.1rem, calc(var(--card-media, 6.5rem) * 0.38));
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--card-ink, #475569);
}

.sensor-card-face__reading-unit {
  font-size: 0.42em;
  font-weight: 600;
  opacity: 0.75;
}

.sensor-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #64748b;
  line-height: 1.25;
}

.dark .sensor-card-face__value {
  color: #94a3b8;
}
</style>
